import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { api } from '@/services/api';
import type {
  ApiEnvelope,
  ChangePasswordRequest,
  LoginRequest,
  MeResponse,
  SellerSummary,
} from '@seller-portal/types';

export const ME_KEY = ['auth', 'me'] as const;

async function fetchMe(): Promise<MeResponse | null> {
  try {
    const res = await api.get<ApiEnvelope<MeResponse>>('/auth/me');
    return res.data.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 401) return null;
    throw error;
  }
}

/** The signed-in person and their sellers; `null` when signed out. */
export function useSession() {
  return useQuery({ queryKey: ME_KEY, queryFn: fetchMe, staleTime: 60_000, retry: false });
}

export function useLogin() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: LoginRequest) =>
      (await api.post<ApiEnvelope<MeResponse>>('/auth/login', body)).data.data,
    onSuccess: (me) => queryClient.setQueryData(ME_KEY, me),
  });
}

export function useLogout() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await api.post('/auth/logout');
    },
    // Only the server can end the session (the cookie is httpOnly), so the client forgets it
    // once the server has confirmed. A failed call leaves the person signed in, and told so.
    onSuccess: () => {
      queryClient.setQueryData(ME_KEY, null);
      queryClient.removeQueries({ predicate: (q) => q.queryKey[0] !== 'auth' });
    },
  });
}

export function useChangePassword() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: ChangePasswordRequest) =>
      (await api.post<ApiEnvelope<MeResponse>>('/auth/change-password', body)).data.data,
    onSuccess: (me) => queryClient.setQueryData(ME_KEY, me),
  });
}

/** Where a person lands after sign-in. */
export function homePathFor(me: MeResponse): string {
  if (me.user.mustChangePassword) return '/account/password';
  // Admins start on Seller support, however many sellers they can open.
  if (me.user.isAdmin) return '/admin/sellers';
  const only = me.sellers.length === 1 ? me.sellers[0] : undefined;
  if (only) return `/${only.id}`;
  return me.sellers.length > 1 ? '/select-seller' : '/no-seller';
}

/** Where the Merx mark and "Go to Home" lead: the seller's Home, else the signed-in landing. */
export function consoleHomePath(
  me: MeResponse | null | undefined,
  seller?: SellerSummary | null
): string {
  if (seller) return `/${seller.id}`;
  return me ? homePathFor(me) : '/login';
}
